import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminAuditLog } from '@terafile/shared'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { UpdateTenantPlanModal } from '../components/UpdateTenantPlanModal.js'
import {
  getAdminTenant,
  listTenantAuditLogs,
  listTenantProjects,
  suspendTenant,
  unsuspendTenant,
} from '../lib/admin-api.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { classifyFile, iconForKind } from '../lib/file-kind.js'
import { LOADING, PLAN_LABEL, TENANT_KIND_LABEL, TENANT_STATUS_LABEL } from '../lib/labels.js'

/**
 * 管理者向けテナント詳細 + ステルス閲覧画面。
 *
 * - 警告バナーで「これはステルス閲覧です」と明示
 * - 停止 / 解除 / プラン変更 の操作を行える
 */
export function AdminTenantDetailPage() {
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const tenantId = id ?? ''
  const [planOpen, setPlanOpen] = useState(false)

  const tenantQ = useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => getAdminTenant(tenantId),
    enabled: !!tenantId,
  })
  const projectsQ = useQuery({
    queryKey: ['admin-tenant-projects', tenantId],
    queryFn: () => listTenantProjects(tenantId),
    enabled: !!tenantId,
  })
  // テナント単位の監査ログ。最初は最新 20 件のみ表示。
  const auditLogsQ = useQuery({
    queryKey: ['admin-tenant-audit-logs', tenantId],
    queryFn: () => listTenantAuditLogs(tenantId, { limit: 20 }),
    enabled: !!tenantId,
  })

  const invalidateTenant = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-tenant', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
  }

  const suspendMut = useMutation({
    mutationFn: () => suspendTenant(tenantId),
    onSuccess: invalidateTenant,
  })
  const unsuspendMut = useMutation({
    mutationFn: () => unsuspendTenant(tenantId),
    onSuccess: invalidateTenant,
  })

  const tenant = tenantQ.data
  const isSuspended = tenant?.status === 'SUSPENDED'
  const isEphemeral = tenant?.kind === 'EPHEMERAL'

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div className="bg-red-50 border border-red-300 rounded-md px-4 py-2 text-sm text-red-900 font-medium">
        ⚠ 監査閲覧モード（記録に残らない閲覧）—
        テナント側のアクセス記録には残らず、運営側の監査ログにのみ記録されます
      </div>
      {tenantQ.error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {toUserMessage(tenantQ.error, FETCH_ERROR_FALLBACK)}
        </div>
      )}

      {tenant && (
        <section>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold">{tenant.name ?? '(無名)'}</h1>
              <p className="mt-1">
                <StatusBadge status={tenant.status} />
              </p>
            </div>
            {/* 操作ボタン群（暫定テナントは手動操作対象外） */}
            {!isEphemeral && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setPlanOpen(true)}
                  className="text-sm px-3 py-1.5 border border-border rounded-md hover:bg-accent/30"
                >
                  プラン変更
                </button>
                {isSuspended ? (
                  <button
                    type="button"
                    onClick={() => unsuspendMut.mutate()}
                    disabled={unsuspendMut.isPending}
                    className="text-sm px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {unsuspendMut.isPending ? '解除中…' : '停止解除'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`${tenant.name ?? '(無名)'} を停止しますか？`)) {
                        suspendMut.mutate()
                      }
                    }}
                    disabled={suspendMut.isPending}
                    className="text-sm px-3 py-1.5 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                  >
                    {suspendMut.isPending ? '停止中…' : '停止'}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* mutation エラー */}
          {(suspendMut.error || unsuspendMut.error) && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {toUserMessage(suspendMut.error || unsuspendMut.error, SUBMIT_ERROR_FALLBACK)}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <Field label="ID">
              <span className="font-mono text-xs">{tenant.id}</span>
            </Field>
            <Field label="種別">{TENANT_KIND_LABEL[tenant.kind]}</Field>
            <Field label="プラン">{PLAN_LABEL[tenant.plan]}</Field>
            <Field label="使用量">{formatBytes(Number(tenant.storageUsedBytes))}</Field>
            <Field label="作成日">{new Date(tenant.createdAt).toLocaleString('ja-JP')}</Field>
            <Field label="更新日">{new Date(tenant.updatedAt).toLocaleString('ja-JP')}</Field>
            {tenant.expiresAt && (
              <Field label="期限">{new Date(tenant.expiresAt).toLocaleString('ja-JP')}</Field>
            )}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-semibold">プロジェクト一覧（監査閲覧）</h2>
        {projectsQ.isLoading && (
          <p className="text-sm text-muted-foreground mt-2">{LOADING.fetching}</p>
        )}
        {projectsQ.error && (
          <p className="text-sm text-red-600 mt-2">{(projectsQ.error as Error).message}</p>
        )}
        {projectsQ.data && (
          <ul className="mt-2 space-y-2">
            {projectsQ.data.items.length === 0 ? (
              <li className="text-sm text-muted-foreground">プロジェクトはありません</li>
            ) : (
              projectsQ.data.items.map((p) => (
                <li
                  key={p.id}
                  className="border border-border rounded-md hover:border-primary hover:bg-accent/30 transition-colors"
                >
                  <Link
                    to={`/admin/tenants/${tenantId}/projects/${p.id}/files`}
                    className="flex items-center gap-3 p-3 group"
                    aria-label={`${p.name} のファイル一覧を監査閲覧`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-primary group-hover:underline">{p.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ファイル {p.fileCount} 件 · 作成日{' '}
                        {new Date(p.createdAt).toLocaleString('ja-JP')}
                        {p.archivedAt && ' · アーカイブ済み'}
                      </p>
                    </div>
                    <span aria-hidden className="text-muted-foreground group-hover:text-primary">
                      →
                    </span>
                  </Link>
                </li>
              ))
            )}
          </ul>
        )}
      </section>

      {/* 監査ログ（このテナントに関する管理者操作の履歴） */}
      <section>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-semibold">監査ログ</h2>
          <p className="text-xs text-muted-foreground">
            このテナントに関連する管理者操作の履歴。最新 20 件。
          </p>
        </div>
        {auditLogsQ.isLoading && (
          <p className="text-sm text-muted-foreground mt-2">{LOADING.fetching}</p>
        )}
        {auditLogsQ.error && (
          <p className="text-sm text-red-600 mt-2">
            {toUserMessage(auditLogsQ.error, FETCH_ERROR_FALLBACK)}
          </p>
        )}
        {auditLogsQ.data && (
          <div className="mt-2 overflow-x-auto border border-border rounded-md">
            {/* min-w を与えることで、狭い画面では親の overflow-x-auto が効いて
                横スクロールになる（w-full だけだとセルが縦に潰れて折り返す）。 */}
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">日時</th>
                  <th className="text-left px-3 py-2">操作</th>
                  <th className="text-left px-3 py-2">管理者</th>
                  <th className="text-left px-3 py-2">対象</th>
                  <th className="text-left px-3 py-2">アクセス元</th>
                </tr>
              </thead>
              <tbody>
                {auditLogsQ.data.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                      記録はありません
                    </td>
                  </tr>
                ) : (
                  auditLogsQ.data.items.map((log: AdminAuditLog) => (
                    <tr key={log.id} className="border-t border-border">
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs editorial-num">
                        {new Date(log.createdAt).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-3 py-2">
                        <AdminActionLabel action={log.action} />
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" title={log.adminUserId}>
                        {log.adminUserEmail ?? (
                          <span className="font-mono text-muted-foreground italic">
                            {log.adminUserId.slice(0, 8)}…
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        <AdminTargetResource log={log} />
                      </td>
                      <td
                        className="px-3 py-2 text-xs font-mono"
                        title={log.userAgent ? `User-Agent: ${log.userAgent}` : undefined}
                      >
                        {/* 管理者画面はテナント側より広い権限を持つため、IP は全オクテット表示。
                            ツールチップで User-Agent も併記。 */}
                        {log.ip ?? '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {auditLogsQ.data.total > auditLogsQ.data.items.length && (
              <p className="text-xs text-muted-foreground p-2 border-t border-border bg-muted/30">
                全 {auditLogsQ.data.total.toLocaleString('ja-JP')} 件のうち{' '}
                {auditLogsQ.data.items.length} 件を表示
              </p>
            )}
          </div>
        )}
      </section>

      {planOpen && tenant && (
        <UpdateTenantPlanModal tenant={tenant} onClose={() => setPlanOpen(false)} />
      )}
    </div>
  )
}

/**
 * 監査ログの操作種別を日本語ラベルに変換して表示する。
 *
 * - DB には英大文字スネークの enum-like 文字列を保存（API/CSV で解釈しやすい）
 * - 表示時のみ日本語化する。未知のキーは生の値を fallback として出す
 * - ステルス操作（管理者がテナントを覗く系）は色を落として可視的に区別
 *
 * 関連: apps/api/src/routes/admin.ts の audit log 起票（同じキーをここで受ける）
 */
const ADMIN_ACTION_LABEL: Record<string, string> = {
  // 一覧 / 参照系
  LIST_TENANTS: 'テナント一覧',
  GET_TENANT: 'テナント詳細',
  GET_DASHBOARD_SUMMARY: 'ダッシュボード閲覧',
  LIST_TENANT_AUDIT_LOGS: '監査ログ閲覧',
  // ステータス変更系
  SUSPEND_TENANT: 'テナント凍結',
  UNSUSPEND_TENANT: '凍結解除',
  UPDATE_TENANT_PLAN: 'プラン変更',
  // 監査閲覧系（テナント側 access_logs には残らない管理者操作）。
  // 内部コードは旧来の STEALTH_ プレフィクスを維持しつつ、表示文言だけ刷新する。
  STEALTH_LIST_PROJECTS: '監査閲覧: プロジェクト一覧',
  STEALTH_LIST_FILES: '監査閲覧: ファイル一覧',
  STEALTH_DOWNLOAD: '監査閲覧: ダウンロード',
  STEALTH_STREAM_PLAY: '監査閲覧: 動画再生',
  STEALTH_VIEW_THUMBNAIL: '監査閲覧: サムネ閲覧',
}

/**
 * 監査ログの「対象」セル表示。
 *
 * 表示優先度:
 *   1) API が解決した `targetName`（プロジェクト名 / ファイル名）→ メイン表示
 *   2) `targetName` が null（削除済み等）→ 「(削除済み) <短縮id>」
 *   3) 互換: 旧スキーマの生 targetResource しか無い場合は "kind: id" 形式
 *   4) 何も無い → 「—」
 *
 * `title` 属性に元の生値（`file:abc-...`）を残してホバーで確認可能にする。
 */
function AdminTargetResource({
  log,
}: {
  log: {
    targetKind: 'file' | 'project' | null
    targetId: string | null
    targetName: string | null
    targetMimeType: string | null
    targetResource: string | null
  }
}) {
  const KIND_LABEL: Record<string, string> = {
    file: 'ファイル',
    project: 'プロジェクト',
  }

  if (log.targetKind && log.targetId) {
    const label = KIND_LABEL[log.targetKind] ?? log.targetKind
    if (log.targetName) {
      const icon =
        log.targetKind === 'file' ? iconForKind(classifyFile(log.targetMimeType ?? '')) : '📁'
      return (
        <span title={log.targetResource ?? undefined} className="inline-flex items-center gap-1.5">
          <span aria-hidden className="text-base leading-none">
            {icon}
          </span>
          <span className="text-muted-foreground">{label}: </span>
          <span className="font-medium">{log.targetName}</span>
        </span>
      )
    }
    // 名前解決できなかった = DB から消えている
    return (
      <span title={log.targetResource ?? undefined} className="text-muted-foreground italic">
        ({label}削除済み) <span className="font-mono">{log.targetId.slice(0, 8)}…</span>
      </span>
    )
  }

  // 旧スキーマの forward-compat（targetKind/targetId が無い古いレスポンス）
  if (log.targetResource) {
    return <span className="break-all">{log.targetResource}</span>
  }
  return <span>—</span>
}

function AdminActionLabel({ action }: { action: string }) {
  const label = ADMIN_ACTION_LABEL[action] ?? action
  const isStealth = action.startsWith('STEALTH_')
  return (
    <span title={action} className={isStealth ? 'text-amber-700 font-medium' : ''}>
      {label}
    </span>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div>{children}</div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  // ラベルは labels.ts の TENANT_STATUS_LABEL を共通参照（issue #36）
  const map: Record<string, { label: string; className: string }> = {
    ACTIVE: {
      label: TENANT_STATUS_LABEL.ACTIVE,
      className: 'bg-green-100 text-green-800 border-green-300',
    },
    FROZEN: {
      label: TENANT_STATUS_LABEL.FROZEN,
      className: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    },
    SUSPENDED: {
      label: TENANT_STATUS_LABEL.SUSPENDED,
      className: 'bg-red-100 text-red-800 border-red-300',
    },
    DELETED: { label: '削除済み', className: 'bg-gray-100 text-gray-800 border-gray-300' },
  }
  const cfg = map[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-800 border-gray-300',
  }
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium border rounded ${cfg.className}`}
    >
      {cfg.label}
    </span>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
